import { selectGlobalBannerHeight } from "@/context/modules/app";
import useAppStore from "@/context/store";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import HBIcon from "../HBIcon";
import styles from "./SideNavGuide.module.less";

const stepKey = "side-nav-guide-step";

const steps = [
  {
    target: "#sidenav-collapse-trigger",
    title: "导航升级为侧边栏模式",
    description: "功能入口更清晰，灵感到创作一键直达",
    cardOffsetY: -100, // 只移动卡片，不移动箭头
    fixedY: 280, // 指定y轴位置
    offsetX: -8,
  },
  {
    target: "#sidenav-item-inspiration",
    title: "「关注」和「发现」合并",
    description: "探索亿万灵感，轻松找到创作方向",
  },
  {
    target: "#sidenav-item-user",
    title: "打造专业的个人主页",
    description: "助力个人品牌建设，高效管理灵感空间",
  },
  {
    target: "#sidenav-item-create",
    title: "原「上传采集」入口调整",
    description: "拖拽秒传灵感图，内容管理更省心",
  },
];

export default function SideNavGuide() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const guideRef = useRef(null);
  const targetRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const globalBannerHeight = useAppStore(selectGlobalBannerHeight);

  useEffect(() => {
    const savedStep = localStorage.getItem(stepKey);

    if (savedStep === null) {
      setIsVisible(true);
    } else {
      const step = parseInt(savedStep, 10);
      if (step >= 0 && step < steps.length) {
        setCurrentStep(step);
        setIsVisible(true);
      }
    }
  }, []);

  const updatePosition = () => {
    const targetElement = document.querySelector(steps[currentStep].target);
    if (!targetElement) return;

    // 移除之前目标元素的事件监听
    if (targetRef.current) {
      targetRef.current.removeEventListener("click", handleTargetClick);
    }

    // 添加新目标元素的事件监听
    targetElement.addEventListener("click", handleTargetClick);
    targetRef.current = targetElement;

    const targetRect = targetElement.getBoundingClientRect();

    const { offsetY = 0, offsetX = 0, fixedY } = steps[currentStep];

    // 计算引导框的位置，放在目标元素的右侧
    const top = fixedY ?? targetRect.top + targetRect.height / 2 + offsetY;
    const left = targetRect.right + offsetX;

    setPosition({ top, left });
  };

  const handleTargetClick = () => {
    goToNext();
  };

  // 元素大小变化和位置变化，以及步骤变化监听
  useEffect(() => {
    if (!isVisible) return;

    // 保存当前步骤
    localStorage.setItem(stepKey, currentStep.toString());

    // 断开之前的观察
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    const targetElement = document.querySelector(steps[currentStep].target);

    if (targetElement) {
      // 创建新的 ResizeObserver 实例
      resizeObserverRef.current = new ResizeObserver(() => {
        updatePosition();
      });

      // 开始观察目标元素
      resizeObserverRef.current.observe(targetElement);
    }

    return () => {
      // 清理目标元素的事件监听
      if (targetRef.current) {
        targetRef.current.removeEventListener("click", handleTargetClick);
      }
      // 断开 ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [isVisible, currentStep, globalBannerHeight]);

  const close = () => {
    setIsVisible(false);
    // 记录用户已完成所有步骤
    localStorage.setItem(stepKey, steps.length);
  };

  const goToNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      close();
    }
  };

  const goToPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isVisible) return null;

  const currentGuide = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div
      ref={guideRef}
      className={classNames(
        styles.guideCard,
        styles[currentGuide.arrowLeftBottom]
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        "--card-offset-y": `${currentGuide.cardOffsetY || 0}px`,
      }}
    >
      <div className={styles.header}>
        <div className={styles.title}>{currentGuide.title}</div>
        <button onClick={close} className={styles.closeButton} type="text">
          <HBIcon type="ic_close" />
        </button>
      </div>

      <div className={styles.description}>{currentGuide.description}</div>

      <div className={styles.footer}>
        <div className={styles.stepIndicator}>
          <em>{currentStep + 1}</em>/{steps.length}
        </div>
        <div className={styles.buttonGroup}>
          {currentStep > 0 && (
            <button onClick={goToPrev} className={classNames(styles.button)}>
              上一步
            </button>
          )}
          <button
            onClick={goToNext}
            className={classNames(styles.button, styles.buttonPrimary)}
          >
            {isLastStep ? "我知道了" : "下一步"}
          </button>
        </div>
      </div>

      <svg
        className={styles.arrow}
        width="6"
        height="20"
        viewBox="0 0 6 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.58012 11.7169L4.62445 15.3996C5.51358 16.4751 6 17.827 6 19.2224L6 0.388485C6 1.78397 5.51358 3.13581 4.62446 4.21137L1.58012 7.89403C0.663094 9.00334 0.663093 10.6076 1.58012 11.7169Z"
          fill="#4D7CFF"
        />
      </svg>
    </div>
  );
}
